<template>
  <Transition name="slide-in">
    <div v-if="isOpen" class="child-navigation">
      <div
        v-for="(group, groupIndex) in groups"
        :key="groupIndex"
        class="group"
      >
        <div class="group__heading">{{ group.title }}</div>
        <div class="child-navigation__links">
          <Button
            v-for="(link, linkIndex) in group.links"
            :key="linkIndex"
            form="underline-invert"
            tag="a"
            :href="link.disabled ? null : link.url"
            :class="getLinkClass(link)"
            text-align="left"
            @click="handleChildNavigationClick(link)"
          >
            {{ link.title }}
            <div class="badges">
              <Badge
                v-for="(badge, index) in link.badges"
                :key="index"
                size="small"
                :theme="badge.theme"
              >
                {{ badge.text }}
              </Badge>
            </div>
          </Button>
        </div>
      </div>
      <NavigationEditorialCards
        v-if="editorialCards"
        :items="editorialCards.items"
        class="child-navigation-panel__editorial-cards"
      />
      <NavigationBanner
        v-else-if="banner"
        :images="banner.images"
        :text="banner.text"
        :url="banner.url"
        :theme="banner.theme || (banner.highlight ? 'highlight' : 'primary')"
        orientation="landscape"
        :align-image-bottom-desktop="banner.alignImageBottomDesktop === true"
        class="child-navigation-panel__banner"
      />
    </div>
  </Transition>
</template>

<script>
import Button from "@/components/ButtonV2.vue";
import Badge from "@/components/BadgeV2.vue";
import { mapState, mapActions } from "vuex";
import { childNavigationClickEvent } from "@/helpers/events";
// import navigationContent from "@/content/navigation.json";
import NavigationBanner from "@/components/navigation/NavigationBanner.vue";
import NavigationEditorialCards from "@/components/navigation/NavigationEditorialCards.vue";

export default {
  name: "ChildNavigationPanel",
  components: {
    Button,
    Badge,
    NavigationBanner,
    NavigationEditorialCards,
  },

  computed: {
    ...mapState("app", ["isMegaNavOpen", "megaNavMenu", "navVersion"]),
    matchedNav() {
      const matchedNav = this.navigation.items.find(
        (item) => item.slug === this.megaNavMenu
      );

      if (!matchedNav) {
        return null;
      }

      return matchedNav;
    },
    editorialCards() {
      const columnWithCards = this.matchedNav?.mega?.columns.find(
        (column) => column.editorialCards?.items?.length > 0
      );

      return columnWithCards?.editorialCards ?? null;
    },
    banner() {
      if (this.editorialCards) {
        return null;
      }

      const firstColumnWithBanner = this.matchedNav?.mega?.columns.find(
        (column) => column.banner
      );

      if (firstColumnWithBanner?.banner) {
        return firstColumnWithBanner.banner;
      }

      // If no banner exists, find the first carousel item
      const firstColumnWithCarousel = this.matchedNav?.mega?.columns.find(
        (column) => column.carousel?.items?.length > 0
      );

      return firstColumnWithCarousel?.carousel?.items[0];
    },
    groups() {
      const columns = this.matchedNav?.mega?.columns;

      if (!columns) {
        return null;
      }

      const groups = columns.reduce((acc, column) => {
        const groups = column.groups || [];
        return [...acc, ...groups];
      }, []);

      return groups;
    },
    panelTitle() {
      return this.matchedNav?.title;
    },
    isOpen() {
      return this.isMegaNavOpen && this.groups;
    },
    navigation() {
      try {
        return require(`@/content/navigationV${this.navVersion}.json`);
      } catch (e) {
        console.error(e);
        return require(`@/content/navigation.json`);
      }
    },
  },
  methods: {
    ...mapActions("app", ["closeMegaNav"]),
    getLinkClass(item) {
      const baseClass = "child-navigation__link";
      const classes = [baseClass];
      if (item.modifier) {
        classes.push(`${baseClass}--${item.modifier}`);
      }
      // Support simple boolean italic flag from JSON
      if (item.italic) {
        classes.push(`${baseClass}--italic`);
      }
      // Support red highlight on child links via JSON flags
      if (item.highlight === true || item.highlightRed === true) {
        classes.push(`${baseClass}--highlight`);
      }
      if (item.disabled) {
        classes.push(`${baseClass}--disabled`);
      }
      return classes.join(" ");
    },
    handleChildNavigationClick(link) {
      if (link.disabled) {
        return;
      }
      childNavigationClickEvent({
        title: link.title,
        url: link.url,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.child-navigation {
  background: white;
  transform: translateX(-100%);

  &__links {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  &__link {
    padding: 0;
    @include editorial-body(14px);
    text-transform: none;
    display: block;
    width: 100%;

    &--highlight {
      color: $color-red;
    }

    &--italic {
      font-style: italic !important;
    }

    &--disabled {
      pointer-events: none;
      opacity: 0.5;
      cursor: not-allowed;
    }

    :deep(.text) {
      display: inline-flex;
      max-width: 100%;
    }

    .badges {
      position: relative;
      top: -5px;
      :deep(.badge) {
        background: none;
        color: $color-red;
      }
    }
  }
}

:deep(.child-navigation__link.button--underline-invert) {
  @include editorial-body(14px);
  text-transform: none;
  padding: 0;
}

.child-navigation-panel__editorial-cards {
  display: block;
  @extend .container;
  margin-top: 40px;

  :deep(.navigation-editorial-cards) {
    max-width: none;
    margin-left: 0;
    border-left: 0;
    padding-left: 0;
  }

  :deep(.navigation-editorial-cards__description) {
    display: none;
  }
}

.child-navigation-panel__banner {
  display: block;
  @extend .container;
}

.group {
  @extend .container;
  margin-bottom: 40px;
}

.group__heading {
  @include utility-body(14px);
  color: $color-grey-dark;
  text-transform: uppercase;
  margin-bottom: 10px;
}

.slide-in-enter-active,
.slide-in-leave-active {
  transform: translateX(-100%);
  transition: transform 0.25s ease-out;
}

.slide-in-enter-from,
.slide-in-leave-to {
  transform: translateX(0%);
}
</style>
